import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    sanitizeRutDigitsOnly,
    commitRutField,
    isLoginRutNumericMode,
    INVALID_RUT_MESSAGE,
    RUT_DIGITS_HINT
} from "../utils/rutUtils";
import { requestPasswordReset } from "../api/auth";

export default function RecoverPassword() {
    const [identifier, setIdentifier] = useState("");
    const [error, setError] = useState(null);
    const [usernameRutError, setUsernameRutError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [identifierTouched, setIdentifierTouched] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const identifierRequiredError = "Debes ingresar usuario o RUT.";

    useEffect(() => {
        document.title = "Recuperar contraseña | Somnus";
        return () => { document.title = "Somnus"; };
    }, []);

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setSubmitAttempted(true);
        setError(null);
        setUsernameRutError(null);

        if (!identifier.trim()) {
            return;
        }

        let finalIdentifier = identifier.trim();
        if (/^[0-9.]+-[0-9kK]$/i.test(finalIdentifier)) {
            finalIdentifier = finalIdentifier.replace(/\./g, '');
        }
        setLoading(true);
        try {
            await requestPasswordReset(finalIdentifier);

            setIsSent(true);
        } catch (err) {
            const serverMessage = err?.response?.data?.identifier?.[0] ||
                err?.response?.data?.detail ||
                "Error al procesar la solicitud.";
            setError(serverMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container-fluid min-vh-100 bg-light d-flex align-items-center justify-content-center pb-5 font-n">
            <div className="card shadow border-0 rounded-4 overflow-hidden" style={{ maxWidth: 500, width: "100%" }}>
                <div className="card-header bg-white border-bottom py-4 text-center">
                    <h1 className="h4 fw-bold mb-1">Recuperar contraseña</h1>
                </div>

                <div className="card-body p-5">
                    {!isSent ? (
                        <form onSubmit={handleRequestReset}>
                            <p className="small mb-4">
                                Ingresa tu RUT o usuario asociado a tu cuenta. Te enviaremos las instrucciones de recuperación a tu correo electrónico registrado.
                            </p>

                            <div className="mb-4">
                                <label className="form-label text-secondary mb-1"><span>Usuario o RUT:</span></label>
                                <input
                                    type="text"
                                    placeholder="Usuario o RUT (ej. 123456789)"
                                    title={RUT_DIGITS_HINT}
                                    className={`form-control ${(usernameRutError || ((submitAttempted || identifierTouched) && !identifier.trim())) ? "is-invalid" : ""}`}
                                    value={identifier}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        // Aplicar sanitización si parece un RUT
                                        if (isLoginRutNumericMode(v)) {
                                            setIdentifier(sanitizeRutDigitsOnly(v));
                                        } else {
                                            setIdentifier(v);
                                        }
                                        if (error) setError(null);
                                        if (usernameRutError) setUsernameRutError(null);
                                    }}
                                    onBlur={(e) => {
                                        setIdentifierTouched(true);
                                        const prev = e.target.value;
                                        if (!prev.trim() || !isLoginRutNumericMode(prev)) {
                                            return;
                                        }
                                        const r = commitRutField(prev);
                                        if (!r.ok) {
                                            setUsernameRutError(r.message || INVALID_RUT_MESSAGE);
                                            return;
                                        }
                                        if (r.formatted) setIdentifier(r.formatted);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.currentTarget.blur();
                                        }
                                    }}
                                    required
                                />
                                {(submitAttempted || identifierTouched) && !identifier.trim() && (
                                    <div className="invalid-feedback d-block">
                                        {identifierRequiredError}
                                    </div>
                                )}
                                {(usernameRutError || error) && (
                                    <div className="invalid-feedback d-block">
                                        {usernameRutError || error}
                                    </div>
                                )}
                                <p className="fs-6 text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                    Si ingresas RUT, solo números y K. Se validará al salir del campo.
                                </p>


                            </div>

                            <button type="submit" className="adm-save-btn w-100 py-2" disabled={loading}>
                                <span>{loading ? "Enviando..." : "Enviar"}</span>
                            </button>
                        </form>
                    ) : (
                        <div className="text-center">
                            <div className="mb-4">
                                <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h5 className="fw-bold mb-4">¡Solicitud enviada!</h5>
                            <p className="text-muted small">
                                Si el usuario existe, recibirás un enlace en tu correo electrónico para restablecer tu clave. No olvides revisar la carpeta de <b>Spam</b>.
                            </p>
                        </div>
                    )}

                    <div className="mt-4 text-center">
                        <Link to="/login" className="text-decoration-none fs-6">
                            <i className="ri-arrow-left-line me-1"></i> Volver al inicio de sesión
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}